import { useAuth0 } from "@auth0/auth0-react";
import { FilterBar } from "components/FilterBar";
import { LoadingSpinner } from "components/LoadingSpinner";
import {
  NOT_SCHEDULED_DATE,
  orderStatusConstants,
  orderStatusMapping,
  orderTypeConstants,
  orderTypeMapping,
  orderTypeShortMapping,
} from "constants/order";
import { events } from "constants/tagManager";
import { useStaffChatPageHints } from "context/StaffChatPageHintsContext";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { parseDate } from "helpers/date";
import { resolveOrderBenefits } from "helpers/orderBenefits";
import { centsToDollars, parsePrice } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import useLocalStorageState from "hooks/useLocalStorageState";
import usePrice from "hooks/usePrice";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { debounce } from "lodash";
import { Steps } from "pages/OrderOverview/utils/constants";
import { CancelOrderDialog } from "pages/OrderView/components/OrderHeader/components/CancelOrderDialog/CancelOrderDialog";
import { Button } from "primereact/button";
import { CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTableSortOrderType, DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoginWithOrderIdAndDOBMutation } from "store/queries/auth";
import { useNotifyMutation } from "store/queries/communications";
import { useGetOrdersQuery } from "store/queries/order";
import { onPublicTokenChange } from "store/slices/auth";
import { onOrderOverviewStepChange } from "store/slices/order";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import { IOrderHistory } from "types/Order/Order";

import { AthenaImportDialog } from "../AthenaImportDialog/AthenaImportDialog";
import { HSTImportDialog } from "../HSTImportDialog/HSTImportDialog";
import { CheckboxFilter } from "./components/CheckboxFilter";
import {
  ColumnsFilter,
  IColumnsFilterOption,
} from "./components/ColumnsFilter";
import { CopyOrderDialog } from "./components/CopyOrderDIalog";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { MonthlyCollections } from "./components/MonthlyCollections";
import { OrderRowDetails } from "./components/OrderRowDetails";
import { RowActionsMenu } from "./components/RowActionsMenu";
import {
  StatusBadge,
  StatusBadgeTone,
  STATUS_BADGE_ICONS,
  STATUS_BADGE_TONES,
} from "./components/StatusBadge";
import { ROW_GAP, ROW_RADIUS } from "./components/styled";
import {
  pickFocusedOrderId,
  toExpandedRows,
  toggledOrderId,
  toggleExpandedOrderIds,
} from "./focusedOrder.helpers";

dayjs.extend(utc);

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

const PageTitle = styled.h1`
  color: var(--brand-black);
  font-size: 2rem;
  font-weight: var(--font-weight-semibold);
`;

const PageSubtitle = styled.p`
  color: var(--brand-gray-600);
`;

// Light purple chip, no border — pairs with the solid Collect Payment button.
const StyledViewButton = styled(Button)`
  &.p-button {
    background: color-mix(
      in srgb,
      var(--brand-purple-500) 12%,
      var(--brand-white)
    );
    border: none;
    color: var(--brand-purple-700);
    font-weight: var(--font-weight-semibold);
  }

  &.p-button:enabled:hover {
    background: color-mix(
      in srgb,
      var(--brand-purple-500) 20%,
      var(--brand-white)
    );
    border: none;
    color: var(--brand-purple-700);
  }
`;

const StyledIconButton = styled(Button)`
  .p-button-icon {
    font-size: 1.2rem;
  }
`;

// CLE-2520 — the pre-re-run Clear Price of an order's latest automatic eligibility re-run, taken
// from its most recent EligibilityRerun order-history row (`benefitValueChanges.prices
// .amountInCents`). Present whenever the order has any re-run, even one that left the price
// unchanged; null only when the order was never re-run. The re-run's *new* price is deliberately
// not surfaced — the order's current patient responsibility already reflects it.
const getLatestRerunOldPrice = (
  orderHistories: IOrderHistory[] | undefined
): number | null => {
  const latestRerun = (orderHistories ?? [])
    .filter((history) => history.type === "EligibilityRerun")
    .reduce<IOrderHistory | null>(
      (latest, history) =>
        !latest ||
        new Date(history.createdAt).getTime() >=
          new Date(latest.createdAt).getTime()
          ? history
          : latest,
      null
    );
  const amount = latestRerun?.benefitValueChanges?.prices?.amountInCents;
  if (!amount) {
    return null;
  }
  return amount.previousValue;
};

// CLE-2520 — the pre-re-run Clear Price, shown quietly on its own line beneath
// the current one so the two amounts never read as a single figure.
const StyledOldClearPrice = styled.div`
  font-size: 0.75rem;
  color: var(--gray-new-500);
`;

// The order's current patient responsibility. With the "Autorun Balance Updates" column enabled the
// balance the latest automatic eligibility re-run replaced sits under it, labelled ("Old $150.00");
// orders that were never re-run show the amount alone either way.
const patientResponsibilityBody = (
  row: {
    orderAmount: number;
    latestRerunOldClearPrice: number | null;
  },
  showOldBalance: boolean
) => (
  <>
    {parsePrice(centsToDollars(row.orderAmount))}
    {showOldBalance && row.latestRerunOldClearPrice !== null && (
      <StyledOldClearPrice>
        {`Old ${parsePrice(centsToDollars(row.latestRerunOldClearPrice))}`}
      </StyledOldClearPrice>
    )}
  </>
);

// The order's total insurance allowable, in cents. `usePrice` holds one order's
// breakdown in state at a time, so the table cannot call it per row — this
// repeats its arithmetic (usePrice.ts `insuranceAmounts`) so the panel and
// OrderView agree on the same order.
const getContractRateInCents = (order: any) => {
  const procedures: any[] =
    order?.gfeQuote?.pricingBreakdown?.procedureBreakdown ?? [];
  const sumOwed = (field: string) =>
    procedures.reduce(
      (total, procedure) => total + (procedure?.owedByPatient?.[field] || 0),
      0
    );

  const proceduresTotalInCents = procedures.reduce(
    (total, procedure) => total + (procedure?.amountInCents || 0),
    0
  );
  const patientPaysInCents =
    order?.gfeQuote?.pricingBreakdown?.totalInCents || 0;
  const insurancePaysInCents = proceduresTotalInCents - patientPaysInCents;

  return (
    sumOwed("deductible") +
    sumOwed("coinsurance") +
    sumOwed("copay") +
    insurancePaysInCents
  );
};

// The distinct CPT codes across an order's procedures, in the order they appear.
const getCptCodes = (serviceDetails: any): string =>
  Array.from(
    new Set(
      ((serviceDetails?.procedures ?? []) as { code?: number | string }[])
        .map((procedure) => procedure?.code)
        .filter((code) => code !== undefined && code !== null && code !== "")
        .map(String)
    )
  ).join(", ");

// Masked in PostHog session recordings, like every other patient identifier here.
const memberIdBody = (row: { memberId: string }) => (
  <span className="sensitive" data-pii="insurance-member-id">
    {row.memberId}
  </span>
);

const accountNumberBody = (row: { accountNumber: string }) => (
  <span className="sensitive" data-pii="account-number">
    {row.accountNumber}
  </span>
);

const PatientInitialsStyled = styled.span`
  flex-shrink: 0;
  width: 36px;
  height: 36px;

  border-radius: 50%;
  background-color: color-mix(
    in srgb,
    var(--brand-purple-700) 12%,
    var(--brand-white)
  );

  color: var(--brand-purple-700);
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
`;

const RowExpanderStyled = styled.button`
  cursor: pointer;

  flex-shrink: 0;
  width: 32px;
  height: 32px;

  border: none;
  border-radius: 50%;
  background-color: transparent;

  color: var(--brand-purple-700);

  &:hover {
    background-color: color-mix(
      in srgb,
      var(--brand-purple-700) 10%,
      var(--brand-white)
    );
  }

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: 2px;
  }
`;

// First letter of the first and last name part, e.g. "Aisha Rahman" -> "AR".
const getPatientInitials = (patientName?: string) =>
  (patientName ?? "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .filter((_, index, parts) => index === 0 || index === parts.length - 1)
    .join("")
    .toUpperCase();

const PaginationBar = styled.div`
  border-top: 1px solid var(--brand-gray-200);
`;

const PaginationSummary = styled.span`
  color: var(--brand-gray-600);
  font-size: 0.875rem;
`;

const StyledPaginator = styled(Paginator)`
  && {
    padding: 0;
    border: none;
    background: transparent;
  }

  && .p-paginator-first,
  && .p-paginator-prev,
  && .p-paginator-next,
  && .p-paginator-last,
  && .p-paginator-page {
    min-width: 2.5rem;
    height: 2.5rem;
    margin: 0 0.25rem;

    border: 1px solid var(--brand-gray-200);
    border-radius: 50%;
    background: var(--brand-white);

    color: var(--brand-gray-500);
  }

  && .p-paginator-page {
    border-color: transparent;
    border-radius: 12px;
    background: transparent;
    color: var(--brand-gray-700);
  }

  && .p-paginator-page:not(.p-highlight):hover {
    background: color-mix(
      in srgb,
      var(--brand-purple-700) 8%,
      var(--brand-white)
    );
    color: var(--brand-purple-700);
  }

  && .p-paginator-page.p-highlight {
    border-color: var(--brand-purple-600);
    background: var(--brand-purple-600);
    color: var(--brand-white);
  }
`;

const StyledPageDataTable = styled(DataTable)`
  .p-sortable-column .p-sortable-column-icon {
    display: none !important;
  }
  .p-datatable-tbody > tr > td.p-highlight {
    background-color: unset;
    color: unset;
  }
  .p-sortable-column .p-column-title {
    display: flex;
    gap: ${sizer(1)};
    align-items: center;
    justify-content: space-between;
  }
  .p-datatable-tbody > tr > td {
    padding: 16px 10px !important;
  }

  /* The && repeats the generated class so these outrank the theme's own
     .p-datatable rules, which are otherwise equally specific. */
  && .p-datatable-wrapper,
  && .p-datatable-thead > tr > th {
    background: transparent;
  }

  && .p-datatable-thead > tr > th {
    padding-top: 0;
    padding-bottom: 0;
    font-size: 0.75rem;
  }

  /* The theme leaves an inset ring on a clicked header; the tinted pill already
     shows which column is active. */
  && .p-datatable-thead > tr > th.p-sortable-column:focus {
    box-shadow: none;
  }

  /* Sorted and hovered columns share one look: the tinted pill sits on the title
     itself, not the <th>, so it hugs the label and its sort arrow instead of
     filling the whole cell. The padding is always on so hovering doesn't shift
     the label. */
  && .p-datatable-thead > tr > th.p-sortable-column .p-column-title {
    padding: 0.5rem 0.75rem;
    border-radius: 12px;
  }

  && .p-datatable-thead > tr > th.p-highlight,
  &&
    .p-datatable-thead
    > tr
    > th.p-sortable-column:not(.p-sortable-disabled):hover {
    background: transparent;
    color: var(--brand-purple-700);
  }

  && .p-datatable-thead > tr > th.p-highlight .p-column-title,
  &&
    .p-datatable-thead
    > tr
    > th.p-sortable-column:not(.p-sortable-disabled):hover
    .p-column-title {
    background-color: color-mix(
      in srgb,
      var(--brand-purple-700) 10%,
      var(--brand-white)
    );
    color: var(--brand-purple-700);
  }

  /* Each row is its own card. A <tr> can't take a radius, so the row's border
     and corners live on its cells, and the gap between rows comes from the
     table's vertical border-spacing. */
  && .p-datatable-table {
    border-collapse: separate;
    border-spacing: 0 ${ROW_GAP}px;
  }

  && .p-datatable-tbody > tr,
  &&.p-datatable-striped .p-datatable-tbody > tr.p-row-odd {
    background-color: var(--brand-white);
  }

  /* drop-shadow rather than box-shadow: it follows the row's painted silhouette,
     so it traces the rounded corners of the end cells. A box-shadow on the <tr>
     would draw a square-cornered rectangle, and one per <td> would seam between
     cells. */
  && .p-datatable-tbody > tr {
    filter: drop-shadow(
        0 1px 1px color-mix(in srgb, var(--brand-black) 4%, transparent)
      )
      drop-shadow(
        0 2px 4px color-mix(in srgb, var(--brand-black) 5%, transparent)
      );
  }

  && .p-datatable-tbody > tr > td {
    border-top: 1px solid var(--brand-gray-200);
    border-bottom: 1px solid var(--brand-gray-200);
    border-width: 1px 0;
  }

  && .p-datatable-tbody > tr > td:first-child {
    border-left: 1px solid var(--brand-gray-200);
    border-left-width: 1px;
    border-top-left-radius: ${ROW_RADIUS}px;
    border-bottom-left-radius: ${ROW_RADIUS}px;
  }

  && .p-datatable-tbody > tr > td:last-child {
    border-right: 1px solid var(--brand-gray-200);
    border-right-width: 1px;
    border-top-right-radius: ${ROW_RADIUS}px;
    border-bottom-right-radius: ${ROW_RADIUS}px;
  }

  /* An expanded row and its details panel read as a single card: the row keeps
     its top corners but squares off at the bottom, and its bottom border stops
     being the card's edge and becomes the divider above the panel.

     Every cell is squared off rather than just the end ones, and with
     !important, so this cannot lose to the rounding rules above no matter which
     cell ends up first or last — the visible columns are user-configurable. */
  && .p-datatable-tbody > tr.is-expanded > td {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  /* The panel paints its own background, side/bottom borders and bottom
     corners, so the cell around it is nothing but a transparent slot — no
     white frame, no padding of its own. */
  && .p-datatable-tbody > tr.p-datatable-row-expansion {
    background-color: transparent;
  }

  && .p-datatable-tbody > tr.p-datatable-row-expansion > td {
    padding: 0 !important;
    border: none;
  }
`;

// CLE-2520 — the "changed balance due to autorun" filter rides in the status panel
// alongside the statuses. It is not an OrderStatus, so it travels under its own
// URL param and its own handler; this sentinel is only how the shared checkbox
// list identifies it.
const RERUN_FILTER_VALUE = "changedByEligibilityRerun";

// Ordered alphabetically by the label the user sees (see orderStatusMapping),
// not by constant name. Refunded is intentionally absent: filtering by it never
// returned results.
const STATUS_OPTIONS = [
  orderStatusConstants.CANCELED,
  orderStatusConstants.EXPIRED,
  orderStatusConstants.NO_BALANCE_DUE,
  orderStatusConstants.PAID,
  orderStatusConstants.PARTIALLY_PAID,
  orderStatusConstants.DRAFT,
  orderStatusConstants.SENT_TO_PATIENT,
].map((value) => ({ value, name: orderStatusMapping[value] }));

// The ABN column — values come from the API (`abnStatus.helpers.ts`), which also sorts by them.
// Empty when the estimate needs no ABN.
const ABN_STATUS_BADGES: Record<
  string,
  { tone: StatusBadgeTone; icon: string }
> = {
  Signed: { tone: "positive", icon: "pi pi-check-circle" },
  Declined: { tone: "negative", icon: "pi pi-times-circle" },
  "Not signed": { tone: "attention", icon: "pi pi-exclamation-triangle" },
};

const abnStatusBody = (row: { abnStatus: string | null }) => {
  const badge = row.abnStatus ? ABN_STATUS_BADGES[row.abnStatus] : undefined;
  if (!row.abnStatus) return " ";
  return (
    <StatusBadge
      label={row.abnStatus}
      tone={badge?.tone ?? "neutral"}
      icon={badge?.icon}
    />
  );
};

const statusBadgeBody = (row: { status: string }) => (
  <StatusBadge
    label={orderStatusMapping[row.status]}
    tone={STATUS_BADGE_TONES[row.status] ?? "neutral"}
    icon={STATUS_BADGE_ICONS[row.status]}
  />
);

const ORDER_TYPE_OPTIONS = [
  {
    value: orderTypeConstants.BUNDLED,
    name: orderTypeShortMapping[orderTypeConstants.BUNDLED],
  },
  {
    value: orderTypeConstants.ED,
    name: orderTypeShortMapping[orderTypeConstants.ED],
  },
  {
    value: orderTypeConstants.PATIENT_RESPONSIBILITY,
    name: orderTypeShortMapping[orderTypeConstants.PATIENT_RESPONSIBILITY],
  },
  {
    value: orderTypeConstants.BARIATRICS,
    name: orderTypeShortMapping[orderTypeConstants.BARIATRICS],
  },
  {
    value: orderTypeConstants.GFE,
    name: orderTypeShortMapping[orderTypeConstants.GFE],
  },
];

// Column show/hide. Every column is listed, in render order — including the two
// action columns, which render as unlabelled controls rather than data.
const COLUMN_DEFS: IColumnsFilterOption[] = [
  { key: "patientName", header: "Patient" },
  { key: "accountNumber", header: "Account / Visit ID" },
  { key: "orderType", header: "Type" },
  { key: "payerName", header: "Payer" },
  { key: "memberId", header: "Insurance Member ID" },
  { key: "createdAt", header: "Date Created" },
  { key: "dateOfService", header: "Date of Service" },
  { key: "status", header: "Status" },
  { key: "abnStatus", header: "ABN" },
  { key: "lastPaymentDate", header: "Paid/Refunded Date" },
  { key: "orderAmount", header: "Patient Balance" },
  // Not a column of its own — it adds the pre-autorun balance to the patient
  // responsibility cell.
  { key: "latestRerun", header: "Autorun Balance Updates" },
  { key: "checkout", header: "Collect Payment" },
  { key: "view", header: "View" },
  { key: "rowActions", header: "Actions Button" },
];

// The heavier columns start hidden; the user opts into them.
const DEFAULT_HIDDEN_COLUMN_KEYS = [
  "payerName",
  "memberId",
  "latestRerun",
  "abnStatus",
];

// Insurance and ABN columns are only populated for GFE orders, so they are
// offered only to accounts that have some.
const GFE_ONLY_COLUMN_KEYS = ["payerName", "memberId", "abnStatus"];

const DEFAULT_VISIBLE_COLUMN_KEYS = COLUMN_DEFS.map(
  (column) => column.key
).filter((key) => !DEFAULT_HIDDEN_COLUMN_KEYS.includes(key));

const VISIBLE_COLUMNS_STORAGE_KEY = "clearhealth_orders_visible_columns_v5";
const PREVIOUS_VISIBLE_COLUMNS_STORAGE_KEY =
  "clearhealth_orders_visible_columns_v4";

// The starting columns for a browser with nothing stored under the v5 key: a
// stored v4 array plus Account / Visit ID (CLE-3011), so existing users keep
// their column choices, or the defaults when there is no usable v4 array. Read
// only — the hook writes v5, and v4 is left in place for an older tab or a
// rollback.
const readInitialVisibleColumns = (): string[] => {
  try {
    const stored = window.localStorage.getItem(
      PREVIOUS_VISIBLE_COLUMNS_STORAGE_KEY
    );
    if (!stored) return DEFAULT_VISIBLE_COLUMN_KEYS;
    const parsed: unknown = JSON.parse(stored);
    if (
      !Array.isArray(parsed) ||
      !parsed.every((key) => typeof key === "string")
    ) {
      return DEFAULT_VISIBLE_COLUMN_KEYS;
    }
    return parsed.includes("accountNumber")
      ? parsed
      : [...parsed, "accountNumber"];
  } catch {
    return DEFAULT_VISIBLE_COLUMN_KEYS;
  }
};

const transformSearchParam = (value: string | null, isDate = false) => {
  if (!value || value === "") return [];
  if (isDate) {
    return value
      .trim()
      .split(",")
      .map((item) => new Date(item.trim()));
  }
  return value
    .trim()
    .split(",")
    .map((item) => item.trim());
};

// Only statuses with nothing paid, so nothing to refund. Canceling a paid
// estimate also refunds it, and the estimate page shows the amount and card
// first — which takes the payment plan and a card lookup this list does not
// load. NoBalanceDue is only ever flipped from SentToPatient when the balance
// drops to $0, so it carries no payment either.
const CANCELABLE_FROM_ROW_MENU = [
  orderStatusConstants.DRAFT,
  orderStatusConstants.SENT_TO_PATIENT,
  orderStatusConstants.EXPIRED,
  orderStatusConstants.NO_BALANCE_DUE,
];

export function OrdersTable({
  accountId,
  directPayEnabled,
  hstImportEnabled,
  athenaImportEnabled,
}: {
  accountId?: string;
  directPayEnabled?: boolean;
  hstImportEnabled?: boolean;
  athenaImportEnabled?: boolean;
}) {
  const { getClearPriceByOrderType } = usePrice();
  const { loginWithRedirect } = useAuth0();
  const query = useQuery();

  const [login] = useLoginWithOrderIdAndDOBMutation();
  const [notify] = useNotifyMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { pageViewEvents } = useAnalytics();
  const { current: toastElement } = useContext(ToastContext);

  const [rows] = useState(25);
  // Initialize first based on page query param (page is 1-based, first is 0-based)
  const pageFromUrl = Number(query.get("page")) || 1;
  const [first, setFirst] = useState((pageFromUrl - 1) * rows);
  const [filters, setFilters] = useState({
    status: transformSearchParam(query.get("status")) as string[],
    orderType: transformSearchParam(query.get("orderType")) as string[],
    patientName: query.get("patientName") || "",
    dateOfService: transformSearchParam(query.get("dateOfService"), true) as
      | Date[]
      | [],
    sortOrder: Number(query.get("sortOrder")) as DataTableSortOrderType,
    sortField: query.get("sortField") || (undefined as string | undefined),
    changedByEligibilityRerun:
      query.get("changedByEligibilityRerun") === "true",
  });
  const [dates, setDates] = useState<Date[]>(
    transformSearchParam(query.get("dateOfService"), true) as Date[] | []
  );
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [showCopyOrderDialog, setShowCopyOrderDialog] = useState(false);
  const [orderToCopy, setOrderToCopy] = useState<string>("");
  const [orderToCancel, setOrderToCancel] = useState<string>("");
  const [cancelNotes, setCancelNotes] = useState("");
  // CLE-3043 — the ONE expansion state: expanded order ids in the order they were opened (newest
  // last). The DataTable's `expandedRows` map (keyed by orderId: with `dataKey` set it looks
  // expansion up by key, not row identity) and the estimate in focus for Clear Health AI are both
  // derived from it. Every toggle goes through `toggleExpanded`.
  const [expandedOrderIds, setExpandedOrderIds] = useState<string[]>([]);
  const expandedRows = useMemo(
    () => toExpandedRows(expandedOrderIds),
    [expandedOrderIds]
  );
  const toggleExpanded = useCallback((orderId: string, open: boolean) => {
    setExpandedOrderIds((previous) =>
      toggleExpandedOrderIds(previous, orderId, open)
    );
  }, []);
  const { setFocusedOrderId } = useStaffChatPageHints();

  // CLE-3015 — the Clear Certified tour's "estimates table" step highlights the header row, not the whole
  // table: driver.js scrolls a highlighted element into view, and the whole table is taller than the space
  // under the filters, so the page jumped away from where the trainee was reading. DataTable takes no
  // attributes for its <thead>, so it is set here, after every render (the table re-renders its own parts).
  const tableRef = useRef<DataTable>(null);
  useEffect(() => {
    tableRef.current
      ?.getTable()
      ?.querySelector("thead")
      ?.setAttribute("data-tour", TourAnchor.EstimatesTable);
  });
  const [showHSTImportDialog, setShowHSTImportDialog] = useState(false);
  const [showAthenaImportDialog, setShowAthenaImportDialog] = useState(false);
  // Persisted globally (not per-account), like the claims table's column picker,
  // so a user's column layout carries across hospitals. The key is versioned:
  // v1 stored only the three then-optional keys, which under the current defs
  // would read as "hide every other column". v3 adds the View column — a
  // stored v2 array predates it, so it would otherwise stay hidden for every
  // existing user despite being on by default. v4 exists because Date of
  // Service became visible by default (CLE-2857). Unlike the earlier bumps it
  // carries a stored v3 array over and adds Date of Service, so users keep
  // their column choices (see readInitialVisibleColumns). v5 exists because
  // Account / Visit ID became visible by default (CLE-3011); it carries a
  // stored v4 array over and adds accountNumber.
  const initialVisibleColumns = useMemo(() => readInitialVisibleColumns(), []);
  const [visibleColumns, setVisibleColumns] = useLocalStorageState<string[]>(
    VISIBLE_COLUMNS_STORAGE_KEY,
    initialVisibleColumns
  );

  useEffect(() => {
    const handler = debounce(() => setDebouncedFilters(filters), 1000);
    handler();
    return () => {
      handler.cancel();
    };
  }, [filters]);

  const {
    data: orders,
    isLoading,
    isFetching,
    isError,
    refetch: refetchOrders,
  } = useGetOrdersQuery({
    accountId,
    page: first,
    pageSize: rows,
    ...debouncedFilters,
  });

  useEffect(() => {
    if (isError) loginWithRedirect();
  }, [isError]);

  const parsedOrders = useMemo(() => {
    if (!orders) return [];

    const { data: ordersToParse } = orders;

    const parsed = ordersToParse.map((order) => {
      const { clearPrice } = getClearPriceByOrderType({
        order,
      });
      const { payments } = order;
      const sortedPayments =
        [...payments].sort((a, b) => {
          const dateA = new Date(a.createdAt || new Date(0)).getTime();
          const dateB = new Date(b.createdAt || new Date(0)).getTime();
          return dateA - dateB;
        }) || [];
      const lastPayment = sortedPayments[sortedPayments.length - 1];
      const latestRerunOldClearPrice = getLatestRerunOldPrice(
        order.orderHistories
      );
      const isGfe = order.orderType === orderTypeConstants.GFE;
      const benefits = resolveOrderBenefits(order);
      return {
        orderId: order.id,
        status: order.status,
        orderType: order.orderType,
        orderAmount: clearPrice,
        latestRerunOldClearPrice,
        dateOfService: parseDate(order.dateOfService),
        patientName: `${order.patient.firstName} ${order.patient.lastName}`,
        accountNumber: order.accountNumber || "",
        payerName: (order.serviceDetails as any)?.payerName || "",
        isGfe,
        // Primary insurance only, and only meaningful for GFE orders. Read off
        // the patient, the way the order forms do — not off the order's own
        // benefitDetails.
        memberId: isGfe ? order.patient?.patientBenefits?.memberID || "" : "",
        cptCodes: getCptCodes(order.serviceDetails),
        // The plan's own benefit values, off the same resolver the order forms
        // use — not the amounts this estimate happened to apply.
        ...(isGfe
          ? {
              contractRateInCents: getContractRateInCents(order),
              deductibleInCents: benefits.remainingDeductibleInCents,
              copayInCents: benefits.flatCopayInCents,
              coinsurancePercent: benefits.coinsurancePercent,
              // Named for the max but holds what is left of it; the Medicare
              // sentinel is rendered as text rather than a dollar amount.
              oopRemainingInCents: benefits.outOfPocketMaxInCents,
            }
          : {}),
        externalId: order.externalId,
        patient: order.patient,
        account: order.account,
        createdAt: parseDate(order.createdAt, false, true),
        lastPaymentDate: lastPayment?.createdAt || null,
        abnStatus: order.abnStatus ?? null,
      };
    });
    return parsed;
  }, [orders]);

  // CLE-2677 — memoised: rebuilding the debounced function every render gave each keystroke its own
  // timer, so nothing was ever coalesced and every character rewrote the URL.
  const debouncedFilterChange = useMemo(
    () => debounce((updateFilter: () => void) => updateFilter(), 500),
    []
  );

  useEffect(
    () => () => debouncedFilterChange.cancel(),
    [debouncedFilterChange]
  );

  function handleOnChangeFilter({
    newValue,
    field,
  }: {
    newValue: CalendarValueType | string | string[];
    field: "patientName" | "status" | "orderType" | "dateOfService";
  }) {
    setFilters((prevData) => ({ ...prevData, [field]: newValue }));
    // CLE-2677 — back to page 1 whenever a filter changes. Every one of these narrows the result
    // set, so staying on page 3 while searching showed "No estimates found." for a search that matched.
    setFirst(0);
    const updateFilter = () => {
      let valueToSet = "";
      if (newValue instanceof Date) {
        valueToSet = newValue.toISOString();
      } else if (Array.isArray(newValue)) {
        const items: (Date | string | null)[] = newValue;
        if (items.every((item) => item instanceof Date)) {
          valueToSet = (items as Date[])
            .map((date) => date.toISOString())
            .join(",");
        } else if (items.every((item) => typeof item === "string")) {
          valueToSet = (items as string[]).join(",");
        } else if (
          items.every((item) => item === null || item instanceof Date)
        ) {
          valueToSet = items
            .filter((item): item is Date => item instanceof Date) // Only convert Date objects
            .map((date) => date.toISOString())
            .join(",");
        }
      } else if (typeof newValue === "string") {
        valueToSet = newValue;
      } else {
        valueToSet = "";
      }
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(field, valueToSet);
      searchParams.delete("page");
      navigate(`?${searchParams.toString()}`, { replace: true });
    };
    debouncedFilterChange(updateFilter);
  }

  // CLE-2520 — the "changed balance due to autorun" toggle. Applied immediately (a single click, not
  // typing) and resets to page 1, since the filter usually shrinks the result set enough that the
  // current page would otherwise be empty.
  //
  function handleOnChangeRerunFilter(checked: boolean) {
    setFilters((prevData) => ({
      ...prevData,
      changedByEligibilityRerun: checked,
    }));
    setFirst(0);
    const searchParams = new URLSearchParams(window.location.search);
    if (checked) {
      searchParams.set("changedByEligibilityRerun", "true");
    } else {
      searchParams.delete("changedByEligibilityRerun");
    }
    searchParams.delete("page");
    navigate(`?${searchParams.toString()}`, { replace: true });
  }

  const onClick = (row: any) => {
    const currentQueryParams = new URLSearchParams(location.search);
    // Remove page parameter when navigating to order view
    currentQueryParams.delete("page");
    if (row.status === orderStatusConstants.DRAFT && accountId) {
      currentQueryParams.set("accountId", accountId);
      const queryString = `?${currentQueryParams.toString()}`;
      // NAVIGATE TO THE REVIEW PAGE WITH THE DRAFT ORDER DATA
      navigate(`/orders/review/${row.orderId}${queryString}`);
      return;
    }
    const queryString = `?${currentQueryParams.toString()}`;
    navigate(`/orders/${row.orderId}${queryString}`);
  };

  const onCheckoutClick = (row: any) => {
    login({
      orderId: row.externalId || "",
      dateOfBirth: new Date(row.patient.dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId: row.orderId }));
        dispatch(
          onOrderOverviewStepChange({
            step: Steps.ORDER_OVERVIEW,
          })
        );
        pageViewEvents(
          {
            accountName: row.account.name,
            orderType: row.orderType,
            communicationMethod: row.patient.preferredContactMethod,
          },
          events.CLICK_ACCEPT_PAYMENT
        );
        navigate(`/order/${row.externalId}?internalUser=true`);
      })
      .catch(() => {
        const detail =
          "Authentication failed. Please check your date of birth.";
        toastElement?.show({
          detail,
          summary: "Error",
          severity: "error",
        });
      });
  };

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", (event.page + 1).toString());
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  // Same destination the menu's View/Review Order item opens: drafts land on the
  // review page, which needs the account id to load.
  const getEstimateUrl = (row: any) => {
    if (row.status === orderStatusConstants.DRAFT) {
      const query = accountId ? `?accountId=${accountId}` : "";
      return `${window.location.origin}/orders/review/${row.orderId}${query}`;
    }
    return `${window.location.origin}/orders/${row.orderId}`;
  };

  const onCopyEstimateUrl = async (row: any) => {
    try {
      await navigator.clipboard.writeText(getEstimateUrl(row));
      toastElement?.show({
        detail: "Estimate URL copied to clipboard.",
        summary: "Copied",
        severity: "success",
      });
    } catch {
      // Clipboard API may fail in non-HTTPS or restricted contexts
      toastElement?.show({
        detail: "Could not copy the estimate URL.",
        summary: "Error",
        severity: "error",
      });
    }
  };

  // Same call the estimate page's "Resend Notification" button makes
  // (PatientDetails.tsx) — re-sends the patient their existing notification over
  // whichever channel they prefer. Nothing about the estimate changes.
  // Returned, not fired and forgotten: the row menu keeps itself open and
  // spinning until this settles.
  const onResendEstimate = (row: any) =>
    notify({ orderId: row.orderId, isResend: true })
      .unwrap()
      .then(() => {
        toastElement?.show({
          summary: "Success",
          severity: "success",
          detail: "Notification resent successfully",
        });
      })
      .catch(() => {
        toastElement?.show({
          summary: "Error",
          severity: "error",
          detail: "Failed to resend notification. Please try again.",
        });
      });

  const isRowExpanded = (row: any) => Boolean(expandedRows[row.orderId]);

  const toggleRowExpansion = (row: any) =>
    toggleExpanded(row.orderId, !isRowExpanded(row));

  // CLE-3043 — publish the estimate in focus to the staff chat: the most recently opened row that
  // is still expanded and on this page of results; cleared when none is, or the table unmounts.
  const focusedOrderId = useMemo(
    () =>
      pickFocusedOrderId(
        expandedOrderIds,
        parsedOrders.map((order) => order.orderId)
      ),
    [expandedOrderIds, parsedOrders]
  );
  useEffect(() => {
    setFocusedOrderId(focusedOrderId);
  }, [focusedOrderId, setFocusedOrderId]);
  useEffect(() => () => setFocusedOrderId(null), [setFocusedOrderId]);

  const orderRowDetailsTemplate = (row: any) => (
    <OrderRowDetails
      row={row}
      onCollectPayment={onCheckoutClick}
      onViewEstimate={onClick}
      onCopyEstimateUrl={onCopyEstimateUrl}
    />
  );

  function orderRowExpander(row: any) {
    const expanded = isRowExpanded(row);
    return (
      <RowExpanderStyled
        type="button"
        aria-label={expanded ? "Collapse estimate" : "Expand estimate"}
        aria-expanded={expanded}
        data-testid="order_row_expander_OrderTable"
        className="flex align-items-center justify-content-center"
        onClick={() => toggleRowExpansion(row)}
      >
        <i className={expanded ? "pi pi-chevron-up" : "pi pi-chevron-down"} />
      </RowExpanderStyled>
    );
  }

  // The row's "..." menu. Edit is in the design but not built yet.
  function orderRowActions(row: any) {
    return (
      <RowActionsMenu
        testId="order_row_actions_OrderTable"
        tourAnchor={TourAnchor.RowMenuFirstRow}
        items={[
          // Drafts reach the review page via the row's Review button, so the
          // menu does not repeat it.
          ...(row.status === orderStatusConstants.DRAFT
            ? []
            : [
                {
                  label: "View Estimate",
                  icon: "pi pi-arrow-up-right",
                  onClick: () => onClick(row),
                },
              ]),
          // Same gate the estimate page puts on its Resend Notification button:
          // there is no notification to resend until the estimate has gone out,
          // and once it is paid or canceled there is nothing to chase.
          ...(row.status === orderStatusConstants.SENT_TO_PATIENT
            ? [
                {
                  label: "Resend Estimate",
                  icon: "pi pi-envelope",
                  onClick: () => onResendEstimate(row),
                },
              ]
            : []),
          {
            label: "Duplicate Estimate",
            icon: "pi pi-clone",
            onClick: () => {
              setOrderToCopy(row.orderId);
              setShowCopyOrderDialog(true);
            },
          },
          {
            label: "Copy Estimate URL",
            icon: "pi pi-link",
            onClick: () => onCopyEstimateUrl(row),
          },
          ...(CANCELABLE_FROM_ROW_MENU.includes(row.status)
            ? [
                {
                  label: "Cancel Estimate",
                  icon: "pi pi-times",
                  onClick: () => {
                    setCancelNotes("");
                    setOrderToCancel(row.orderId);
                  },
                },
              ]
            : []),
        ]}
      />
    );
  }

  const onSort = (e: any) => {
    const { sortField } = e;
    let newSortOrder = 1 as DataTableSortOrderType;
    let newSortField = sortField;
    if (filters.sortOrder === 1) {
      newSortOrder = -1;
    } else if (filters.sortOrder === -1) {
      newSortOrder = 0;
      newSortField = undefined;
    }

    setFilters((prevData) => ({
      ...prevData,
      sortField: newSortField || undefined,
      sortOrder: newSortOrder,
    }));
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("sortField", newSortField);
    searchParams.set("sortOrder", newSortOrder?.toString() || "");
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  function sortableColumnHeader(
    title: string,
    isSorted: boolean,
    field: string
  ) {
    let iconClass = "pi pi-sort-alt";
    if (isSorted) {
      iconClass =
        filters.sortOrder === 1
          ? "pi pi-sort-amount-up"
          : "pi pi-sort-amount-down";
    }
    return (
      <>
        {title}
        <i
          className={iconClass}
          tabIndex={0}
          role="button"
          aria-label="Sort by field name"
          title="Sort by field name"
          onClick={() => onSort({ sortField: field })}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onSort({ sortField: field });
            }
          }}
        />
      </>
    );
  }

  function orderCheckoutAction(row: any) {
    // Drafts ("Pending Review") have nothing to collect yet; the View column
    // gives them a Review button into the draft review page instead.
    if (row.status !== orderStatusConstants.SENT_TO_PATIENT) {
      return null;
    }
    return (
      <Button
        className="p-button-sm p-button-rounded-md white-space-nowrap"
        data-testid="order_action_OrderTable"
        data-tour={TourAnchor.CollectPaymentFirstRow}
        label="Collect Payment"
        onClick={() => onCheckoutClick(row)}
      />
    );
  }

  // Same destination as the row menu's "View Estimate": drafts open the review
  // page, everything else the estimate page.
  function orderViewAction(row: any) {
    const isDraft = row.status === orderStatusConstants.DRAFT;

    return (
      <StyledViewButton
        className="p-button-sm p-button-rounded-md white-space-nowrap"
        data-testid={
          isDraft
            ? "order_review_action_OrderTable"
            : "order_view_action_OrderTable"
        }
        data-tour={TourAnchor.ViewFirstRow}
        label={isDraft ? "Review" : "View"}
        onClick={() => onClick(row)}
      />
    );
  }

  function getPatientName(row: any) {
    return (
      <div
        className="sensitive flex align-items-center gap-2"
        data-pii="patient-name"
      >
        <PatientInitialsStyled className="flex align-items-center justify-content-center">
          {getPatientInitials(row.patientName)}
        </PatientInitialsStyled>
        <span>{row.patientName}</span>
      </div>
    );
  }

  // The insurance columns are only meaningful for accounts that have GFE
  // orders, so they drop out of the picker entirely rather than sitting there
  // as checkboxes that toggle columns which never render.
  const columnOptions = useMemo(
    () =>
      COLUMN_DEFS.filter(
        (column) =>
          !GFE_ONLY_COLUMN_KEYS.includes(column.key) || Boolean(orders?.hasGfe)
      ),
    [orders?.hasGfe]
  );

  const isColumnAvailable = (key: string) =>
    columnOptions.some((column) => column.key === key);

  // Accounts rarely use every order type. The API reports which types this
  // account actually has — across all of its orders, not just this page — and
  // the filter offers only those. Before the first response lands we have no
  // list yet, so everything stays on offer rather than flickering.
  const orderTypeOptions = useMemo(() => {
    if (!orders?.orderTypes) {
      return ORDER_TYPE_OPTIONS;
    }
    return ORDER_TYPE_OPTIONS.filter((option) =>
      orders.orderTypes?.includes(option.value)
    );
  }, [orders?.orderTypes]);

  // Same idea for statuses: an account that has never had a partially paid
  // order is not offered "Partially Paid" as a filter that matches nothing.
  // Until the first response lands the full list stays on offer.
  const statusOptions = useMemo(() => {
    if (!orders?.statuses) {
      return STATUS_OPTIONS;
    }
    return STATUS_OPTIONS.filter((option) =>
      orders.statuses?.includes(option.value)
    );
  }, [orders?.statuses]);

  // The autorun filter sits at the bottom of the same panel, and only for
  // accounts an autorun has actually moved a balance for — anywhere else it
  // could only ever filter the table down to nothing.
  const statusFilterOptions = useMemo(
    () =>
      orders?.hasAutorunBalanceUpdates
        ? [
            ...statusOptions,
            { value: RERUN_FILTER_VALUE, name: "Balance Changed by Autorun" },
          ]
        : statusOptions,
    [statusOptions, orders?.hasAutorunBalanceUpdates]
  );

  const statusFilterValue = filters.changedByEligibilityRerun
    ? [...(filters.status ?? []), RERUN_FILTER_VALUE]
    : filters.status;

  // The two halves of the panel go to different places — statuses to the
  // `status` param (debounced with the rest of the filters), the autorun flag to
  // its own — so each is only touched when it actually changed. "Any status"
  // clears both, which is what an empty selection should mean.
  const handleOnChangeStatusFilter = (newValues: string[]) => {
    const newStatuses = newValues.filter(
      (value) => value !== RERUN_FILTER_VALUE
    );
    const newRerun = newValues.includes(RERUN_FILTER_VALUE);

    if (newRerun !== filters.changedByEligibilityRerun) {
      handleOnChangeRerunFilter(newRerun);
    }
    if (newStatuses.join(",") !== (filters.status ?? []).join(",")) {
      handleOnChangeFilter({ newValue: newStatuses, field: "status" });
    }
  };

  // One type is no choice at all, so the filter goes away — unless the URL
  // carries a selection the account cannot match, which the user needs the
  // filter to clear.
  const showOrderTypeFilter =
    orderTypeOptions.length > 1 ||
    (filters.orderType ?? []).some(
      (type) => !orderTypeOptions.some((option) => option.value === type)
    );

  function renderColumn(key: string) {
    switch (key) {
      case "patientName":
        return (
          <Column
            key={key}
            field="patientName"
            header="PATIENT"
            headerStyle={{ width: "15%" }}
            body={(row) => getPatientName(row)}
          />
        );
      case "accountNumber":
        return (
          <Column
            key={key}
            field="accountNumber"
            header="ACCOUNT / VISIT ID"
            body={accountNumberBody}
            headerStyle={{ width: "8%" }}
          />
        );
      case "orderType":
        return (
          <Column
            key={key}
            field="orderType"
            header="TYPE"
            body={(row) => orderTypeShortMapping[row.orderType]}
            headerStyle={{ width: "10%" }}
          />
        );
      case "payerName":
        return (
          <Column
            key={key}
            field="payerName"
            header="PAYER"
            headerStyle={{ width: "12%" }}
          />
        );
      case "memberId":
        return (
          <Column
            key={key}
            field="memberId"
            header="INSURANCE MEMBER ID"
            body={memberIdBody}
            headerStyle={{ width: "12%" }}
          />
        );
      case "createdAt":
        return (
          <Column
            key={key}
            field="createdAt"
            header="DATE CREATED"
            headerStyle={{ width: "10%" }}
          />
        );
      case "dateOfService":
        return (
          <Column
            key={key}
            field="dateOfService"
            className={
              filters.sortField === "dateOfService" ? "p-highlight" : ""
            }
            header={sortableColumnHeader(
              "DOS",
              filters.sortField === "dateOfService",
              "dateOfService"
            )}
            body={(row) =>
              row.dateOfService === NOT_SCHEDULED_DATE
                ? "Not scheduled"
                : row.dateOfService
            }
            sortable
            headerStyle={{ width: "10%" }}
          />
        );
      case "status":
        return (
          <Column
            key={key}
            field="status"
            header={<span data-tour={TourAnchor.StatusColumn}>STATUS</span>}
            body={statusBadgeBody}
            headerStyle={{ width: "10%" }}
          />
        );
      case "abnStatus":
        return (
          <Column
            key={key}
            field="abnStatus"
            className={filters.sortField === "abnStatus" ? "p-highlight" : ""}
            header={sortableColumnHeader(
              "ABN",
              filters.sortField === "abnStatus",
              "abnStatus"
            )}
            body={abnStatusBody}
            sortable
            headerStyle={{ width: "8%" }}
          />
        );
      case "lastPaymentDate":
        return (
          <Column
            key={key}
            field="lastPaymentDate"
            className={
              filters.sortField === "lastPaymentDate" ? "p-highlight" : ""
            }
            header={sortableColumnHeader(
              "PAID/REFUNDED DATE",
              filters.sortField === "lastPaymentDate",
              "lastPaymentDate"
            )}
            body={(row) =>
              row.lastPaymentDate
                ? parseDate(row.lastPaymentDate, false, true)
                : " "
            }
            sortable
            headerStyle={{ width: "10%" }}
          />
        );
      case "orderAmount":
        return (
          <Column
            key={key}
            field="orderAmount"
            header="PATIENT BALANCE"
            body={(row) =>
              patientResponsibilityBody(
                row,
                visibleColumns.includes("latestRerun")
              )
            }
            headerStyle={{ width: "12%" }}
          />
        );
      // Renders nothing on its own; it only turns on the old balance inside the
      // patient responsibility cell above.
      case "latestRerun":
        return null;
      case "checkout":
        return (
          <Column
            key={key}
            header=""
            field="checkout"
            body={(row) => orderCheckoutAction(row)}
            headerStyle={{ width: "5%" }}
          />
        );
      case "view":
        return (
          <Column
            key={key}
            header=""
            field="view"
            body={(row) => orderViewAction(row)}
            headerStyle={{ width: "4%" }}
          />
        );
      case "rowActions":
        return (
          <Column
            key={key}
            header=""
            field="rowActions"
            body={(row) => orderRowActions(row)}
            headerStyle={{ width: "4%" }}
          />
        );
      default:
        return null;
    }
  }

  // CLE-2677 — only the FIRST load swaps the whole table for a spinner. Refetches (every filter
  // change, including each debounced keystroke in the search box) keep the filter bar mounted and
  // show the DataTable's own loading overlay instead: unmounting the input mid-typing threw away
  // the caret, so a long account number could never be typed out.
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <CopyOrderDialog
        showCopyOrderDialog={showCopyOrderDialog}
        setShowCopyOrderDialog={setShowCopyOrderDialog}
        setOrderToCopy={setOrderToCopy}
        orderId={orderToCopy}
        accountId={accountId}
      />
      <CancelOrderDialog
        showCancelDialog={Boolean(orderToCancel)}
        setShowCancelDialog={(show: boolean) => {
          if (!show) setOrderToCancel("");
        }}
        cancelNotes={cancelNotes}
        setCancelNotes={setCancelNotes}
        orderId={orderToCancel}
        onUpdateOrder={refetchOrders}
        cancelOrderRefundAmount={0}
        cardLastDigits={undefined}
        isLoading={false}
        setIsAuthorizationDone={() => {}}
      />
      {accountId && (
        <HSTImportDialog
          visible={showHSTImportDialog}
          onHide={() => setShowHSTImportDialog(false)}
          accountId={accountId}
        />
      )}
      <AthenaImportDialog
        visible={showAthenaImportDialog}
        onHide={() => setShowAthenaImportDialog(false)}
        accountId={accountId}
      />
      <div className="flex flex-wrap align-items-center justify-content-between gap-4 mb-4">
        <div>
          <PageTitle className="m-0">Patient Estimates</PageTitle>
          <PageSubtitle className="mt-1 mb-0">
            Create, manage, and collect patient estimates.
          </PageSubtitle>
        </div>
        <div className="flex flex-wrap align-items-center gap-2">
          {directPayEnabled && (
            <StyledButton
              data-testid="direct_pay_order_OrderTable"
              data-tour={TourAnchor.DirectPay}
              icon="pi pi-credit-card"
              className="p-button-neutral p-button-sm p-button-rounded-md p-button-shadow"
              onClick={() =>
                navigate(`/order/direct-pay?accountId=${accountId}`)
              }
            >
              Direct Pay
            </StyledButton>
          )}
          {hstImportEnabled && (
            <StyledButton
              data-testid="hst_import_OrderTable"
              icon="pi pi-upload"
              className="p-button-neutral p-button-sm p-button-rounded-md p-button-shadow"
              onClick={() => setShowHSTImportDialog(true)}
            >
              Import from CSV
            </StyledButton>
          )}
          {athenaImportEnabled && (
            <StyledButton
              data-testid="athena_import_OrderTable"
              icon="pi pi-upload"
              className="p-button-neutral p-button-sm p-button-rounded-md p-button-shadow"
              onClick={() => setShowAthenaImportDialog(true)}
            >
              Import CSV
            </StyledButton>
          )}
          <StyledButton
            data-testid="create_new_order_OrderTable"
            data-tour={TourAnchor.NewEstimate}
            icon="pi pi-plus"
            className="p-button-raised p-button-sm p-button-rounded-md"
            onClick={() => navigate(`/orders/new?accountId=${accountId}`)}
          >
            New Estimate
          </StyledButton>
        </div>
      </div>

      <MonthlyCollections accountId={accountId} />
      <FilterBar className="flex flex-wrap align-items-center gap-2 p-2 mb-4">
        <span className="p-input-icon-left flex-1">
          <i className="pi pi-search" />
          {/* CLE-2677 — one box; the server decides what the string matches, and the query param
              keeps its old `patientName` name so existing links and bookmarks still work.
              The placeholder names only the three we tell people about. The backend also matches the
              MRN / member ID and the estimate ID (`externalId` or the order uuid) — both still work
              if typed, they're just no longer advertised. Estimate ID was dropped from the wording
              deliberately, the backend clause kept in case it's wanted again. */}
          <InputText
            className="w-full"
            data-testid="name_multiselect_OrderTable"
            data-tour={TourAnchor.SearchBox}
            id="patientSearch"
            value={filters.patientName}
            placeholder="Search by patient name, payer, or account number"
            title="Search by patient name (any order), account number, estimate ID, or — on the primary insurance only — MRN / member ID or payer"
            onChange={(e) =>
              handleOnChangeFilter({
                newValue: e.target.value,
                field: "patientName",
              })
            }
          />
        </span>
        {showOrderTypeFilter && (
          <CheckboxFilter
            label="Type:"
            allLabel="Any type"
            testIdPrefix="type"
            options={orderTypeOptions}
            value={filters.orderType}
            onChange={(newOrderTypes) =>
              handleOnChangeFilter({
                newValue: newOrderTypes,
                field: "orderType",
              })
            }
          />
        )}
        <DateRangeFilter
          tourAnchor={TourAnchor.DateFilter}
          value={dates}
          onChange={(newDates) => {
            setDates(newDates);
            handleOnChangeFilter({
              field: "dateOfService",
              newValue: newDates,
            });
          }}
        />
        <CheckboxFilter
          label="Filter by:"
          allLabel="Any status"
          testIdPrefix="status"
          tourAnchor={TourAnchor.StatusFilter}
          options={statusFilterOptions}
          value={statusFilterValue}
          onChange={handleOnChangeStatusFilter}
        />
        <ColumnsFilter
          options={columnOptions}
          value={visibleColumns.filter(isColumnAvailable)}
          onChange={(keys) =>
            // Preferences for columns this account does not offer (Payer on a
            // non-GFE account) are kept, so they survive a hop to a hospital
            // that does offer them.
            setVisibleColumns([
              ...keys,
              ...visibleColumns.filter((key) => !isColumnAvailable(key)),
            ])
          }
        />
      </FilterBar>

      <StyledPageDataTable
        ref={tableRef}
        emptyMessage="No estimates found."
        stripedRows
        className="p-fluid"
        value={parsedOrders}
        loading={isFetching}
        onRowClick={({ data, originalEvent }) =>
          originalEvent.detail > 1 && onClick(data)
        }
        removableSort
        sortMode="single"
        onSort={onSort}
        responsiveLayout="scroll"
        dataKey="orderId"
        rowClassName={(row: any) => (isRowExpanded(row) ? "is-expanded" : "")}
        expandedRows={expandedRows}
        onRowToggle={({ data }) => {
          const toggled = toggledOrderId(
            expandedRows,
            data as Record<string, boolean>
          );
          if (toggled) toggleExpanded(toggled.orderId, toggled.open);
        }}
        rowExpansionTemplate={orderRowDetailsTemplate}
      >
        {columnOptions
          .filter((column) => visibleColumns.includes(column.key))
          .map((column) => renderColumn(column.key))}
        {/* Structural, so it stays out of the column picker: hiding it would
            strand any expanded row with no way to close it. */}
        <Column
          header=""
          field="rowExpander"
          body={(row) => orderRowExpander(row)}
          headerStyle={{ width: "4%" }}
        />
      </StyledPageDataTable>
      <PaginationBar className="flex flex-wrap align-items-center justify-content-between gap-3 pt-3">
        <PaginationSummary>
          {`Showing ${orders?.total ? first + 1 : 0}–${Math.min(
            first + rows,
            orders?.total ?? 0
          )} of ${orders?.total ?? 0} estimates`}
        </PaginationSummary>
        <StyledPaginator
          first={first}
          rows={rows}
          totalRecords={orders?.total}
          onPageChange={onPageChange}
        />
      </PaginationBar>
    </div>
  );
}
